import { useRef } from 'react';

// "<name> is typing" line, sticky above the composer on mobile. Driven by the
// presence `typers` map; holds the last text in a local ref so it fades out
// smoothly instead of snapping to empty.
type TypingIndicatorProps = {
  currentVoidId: string;
  typers: Record<string, string>;
  isMdUp: boolean;
  keyboardInset: number;
  composerHeight: number;
};

function TypingIndicator({
  currentVoidId,
  typers,
  isMdUp,
  keyboardInset,
  composerHeight,
}: TypingIndicatorProps) {
  const lastTypingTextRef = useRef<string>('');
  return (
    <div className="px-4 text-xs text-emerald-300 md:mb-2">
      <div
        className="py-1 h-6 md:static"
        style={
          !isMdUp
            ? {
                position: 'fixed',
                bottom:
                  keyboardInset === 0
                    ? `calc(${composerHeight + 16}px + env(safe-area-inset-bottom))`
                    : `${composerHeight + 16}px`,
                left: 20,
                right: 0,
                zIndex: 50,
              }
            : undefined
        }
      >
        {(() => {
          if (!currentVoidId) return null;
          // Use last non-empty text so we can fade it out smoothly
          const names = Object.values(typers);
          const hasNames = names.length > 0;
          const base = hasNames
            ? (names.length === 1
                ? `${names[0]} is typing`
                : `${names.slice(0,2).join(', ')}${names.length>2 ? ' +' + (names.length-2) : ''} are typing`)
            : '';
          if (hasNames) lastTypingTextRef.current = base;
          const text = hasNames ? base : lastTypingTextRef.current;
          return (
            <span
              className={`inline-flex items-center gap-2 transition-opacity duration-200 ease-out ${hasNames ? 'opacity-100' : 'opacity-0'}`}
              aria-live="polite" aria-atomic="true"
            >
              <span>{text}</span>
              <span className="typing-dots" aria-hidden="true">
                <span className="typing-dot"></span>
                <span className="typing-dot"></span>
                <span className="typing-dot"></span>
              </span>
            </span>
          );
        })()}
      </div>
    </div>
  );
}

export default TypingIndicator;
